import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, Form } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';

const Coupons = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);

    const statusMap = {
        0: { text: '正常', type: 'success' },
        1: { text: '下架', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/shop/list_shop_coupon/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/shop/add_shop_coupon/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/shop/edit_shop_coupon/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/shop/delete_shop_coupon/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的优惠券');
            return;
        }
        try {
            const response = await request.post('/go/sp/shop/batchedit_shop_coupon/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '优惠券信息',
            dataIndex: 'name',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {record.image && <img src={record.image} alt={text} style={{ width: 40, height: 40, borderRadius: 4 }} />}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                        {record.code && <div style={{ color: 'var(--semi-color-text-2)' }}>优惠码: {record.code}</div>}
                    </div>
                </div>
            )
        },
        {
            title: '优惠规则',
            dataIndex: 'rule',
            width: 200,
            render: (rule) => {
                if (!rule) return '-';
                let ruleText = '';
                if (rule.discount_fee) {
                    ruleText = `减${(rule.discount_fee / 100).toFixed(2)}元`;
                } else if (rule.discount_rate) {
                    ruleText = `打${rule.discount_rate}折`;
                }
                if (rule.min_fee) {
                    ruleText += `，满${(rule.min_fee / 100).toFixed(2)}元可用`;
                }
                return ruleText;
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '数量',
            dataIndex: 'publish_cnt',
            width: 150,
            render: (text, record) => `${text}/${record.max_cnt}`
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该优惠券吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            rule: {
                ...(currentRecord.rule || {}),
                min_fee: (currentRecord.rule?.min_fee || 0) / 100,
                discount_fee: (currentRecord.rule?.discount_fee || 0) / 100,
                duration: Math.floor((currentRecord.rule?.duration || 0) / (24 * 60 * 60))
            }
        } : {
            name: '',
            image: '',
            code: '',
            status: 0,
            max_cnt: '',
            publish_cnt: '0',
            rule: {
                any: 0,
                multi: 0,
                min_fee: 0,
                discount_fee: 0,
                discount_rate: 0,
                duration: 0
            }
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    // 转换金额单位为分
                    const submitValues = {
                        ...values,
                        max_cnt: values.max_cnt.toString(),
                        publish_cnt: values.publish_cnt.toString(),
                        rule: {
                            ...values.rule,
                            min_fee: Math.round(values.rule.min_fee * 100),
                            discount_fee: Math.round(values.rule.discount_fee * 100),
                            duration: values.rule.duration * 24 * 60 * 60, // 天转秒
                            any: values.rule.any ? 1 : 0,
                            multi: values.rule.multi ? 1 : 0
                        }
                    };
                    if (isEdit) {
                        handleEdit(submitValues);
                    } else {
                        handleAdd(submitValues);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="name"
                    label="优惠券名称"
                    rules={[{ required: true, message: '请输入优惠券名称' }]}
                />
                <Form.Slot label="优惠券图标">
                    <ImageSelector
                        value={formApi?.getValue('image')}
                        onChange={value => formApi?.setValue('image', value)}
                        placeholder="请输入优惠券图标URL"
                        width="100%"
                        previewSize={100}
                    />
                </Form.Slot>
                <Form.Input
                    field="code"
                    label="优惠码"
                    placeholder="选填"
                />
                <Form.Input
                    field="max_cnt"
                    label="总数量"
                    rules={[{ required: true, message: '请输入总数量' }]}
                    placeholder="请输入总数量"
                />
                <Form.Select
                    field="status"
                    label="状态"
                    initValue={0}
                >
                    <Select.Option value={0}>正常</Select.Option>
                    <Select.Option value={1}>下架</Select.Option>
                </Form.Select>
                <Form.Slot label="优惠规则">
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <Form.InputNumber
                            field="rule.discount_fee"
                            label="优惠金额(元)"
                            min={0}
                            precision={2}
                            style={{ flex: 1 }}
                        />
                        <Form.InputNumber
                            field="rule.discount_rate"
                            label="折扣率"
                            min={0}
                            max={100}
                            style={{ flex: 1 }}
                        />
                    </div>
                    <Form.InputNumber
                        field="rule.min_fee"
                        label="最低消费(元)"
                        min={0}
                        precision={2}
                    />
                    <Form.InputNumber
                        field="rule.duration"
                        label="有效期(天)"
                        min={0}
                        precision={0}
                    />
                    <Form.Switch
                        field="rule.any"
                        label="不限条件"
                        initValue={0}
                    />
                    <Form.Switch
                        field="rule.multi"
                        label="允许叠加"
                        initValue={0}
                    />
                </Form.Slot>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.couponsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索优惠券名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="优惠券状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增优惠券</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量下架</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量上架</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个优惠券吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增优惠券"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑优惠券"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default Coupons;
